import type { ClipboardEvent } from 'react'

export function handlePasteFiles(onFiles: (files: File[]) => void): (e: ClipboardEvent) => void {
  return (e) => {
    const files = e.clipboardData?.files
    if (!files || files.length === 0) return
    e.preventDefault()
    onFiles(Array.from(files))
  }
}

/**
 * A raw image on the clipboard has no name of its own, so every browser invents
 * the same one — `image.png` — and a run of pastes would land as a stack of
 * identical rows. Rename only that synthetic name: a file COPIED from a file
 * manager also arrives via paste, and its real name is worth keeping.
 */
const CLIPBOARD_IMAGE_NAME = /^image\.(png|jpe?g|webp|gif)$/i

export function nameClipboardFiles(files: File[], prefix: string, now: Date): File[] {
  const pad = (value: number) => String(value).padStart(2, '0')
  const stamp =
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ` +
    `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`

  // One paste can carry several images, and they'd share a whole-second stamp;
  // number them from the second one so a multi-image paste stays distinguishable.
  let renamed = 0
  return files.map((file) => {
    if (!CLIPBOARD_IMAGE_NAME.test(file.name)) return file
    const extension = file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase()
    const suffix = renamed > 0 ? ` (${renamed + 1})` : ''
    renamed += 1
    return new File([file], `${prefix} ${stamp}${suffix}.${extension}`, {
      type: file.type,
      lastModified: file.lastModified,
    })
  })
}
